export const removeMetadata = (id: string) => {
    if (typeof window === "undefined") {
        return;
    }

    const checkAndRemoveScripts = () => {
        const scriptList = document.querySelectorAll("script[type='application/ld+json']");

        if (scriptList.length === 0) {
            setTimeout(checkAndRemoveScripts, 100);
            return;
        }

        scriptList.forEach(script => {
            const scriptId = script.getAttribute("id");

            if (scriptId === id) {
                script.parentNode?.removeChild(script);
            }
        });
    };

    if (document.readyState === "complete") {
        checkAndRemoveScripts();
    } else {
        window.addEventListener("load", checkAndRemoveScripts);
    }
};

export const injectMetadata = (metadata: string, id: string) => {
    if (typeof window === "undefined") {
        return;
    }

    let jsonLdScript = document.querySelector('script[type="application/ld+json"]:not([id])');
    if (!jsonLdScript) {
        jsonLdScript = document.createElement("script");
        jsonLdScript.setAttribute("type", "application/ld+json");
        document.head.appendChild(jsonLdScript);
    }

    let scriptTag = document.getElementById(id);
    if (scriptTag) {
        scriptTag.textContent = metadata;
    } else {
        scriptTag = document.createElement("script");
        scriptTag.setAttribute("type", "application/ld+json");
        scriptTag.id = id;
        scriptTag.textContent = metadata;
        document.head.appendChild(scriptTag);
    }
};

export const setProductMetadata = (props: any) => {
    const {
        productState: { value: product },
        productInfo,
        catalogPageState,
        logo,
        currentUrl,
    } = props;

    const productData = {
        "@context": "https://schema.org/",
        "@type": "Product",
        name: product?.productNumber,
        image: product?.largeImagePath,
        description: product?.productTitle,
        brand: {
            "@type": "Brand",
            name: product?.brand?.name,
        },
        mpn: product?.productNumber,
        offers: {
            "@type": "Offer",
            url: catalogPageState.value.uri,
            priceCurrency: "USD",
            price: productInfo != null ? productInfo.pricing?.actualPrice ?? 0 : 0,
        },
    } as any;

    const breadCrums = catalogPageState.value.breadCrumbs;
    const breadCrumbListData = {
        "@context": "https://schema.org/",
        "@type": "BreadcrumbList",
        itemListElement: breadCrums?.map((subCategory: any, index: number) => ({
            "@type": "ListItem",
            position: index + 1,
            name: subCategory?.text,
            item: `${currentUrl}${subCategory?.url != null ? subCategory?.url : ""}`,
        })),
    } as any;

    const organizationData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Iewc",
        alternateName: "",
        url: `${currentUrl}`,
        logo: `${logo}`,
        sameAs: [""],
    } as any;

    setTimeout(() => {
        injectMetadata(JSON.stringify(productData), "productDetail_ProductSchema");
        injectMetadata(JSON.stringify(breadCrumbListData), "productDetail_BreadCrumbListSchema");
        injectMetadata(JSON.stringify(organizationData), "productDetail_OrganizationSchema");
    }, 500);

    setWebsiteMetadata({ currentUrl });
};

export const setProductListMetadata = (props: any) => {
    const { products, catalogPageState, currentUrl, productInfosByProductId } = props;
    let logo = "";
    if (typeof document !== "undefined") {
        logo = document.querySelector(".home-page-link img")?.getAttribute("src") || "";
    }

    const itemListData = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: products?.map((product: any, index: number) => ({
            "@type": "ListItem",
            position: index + 1,
            name: product?.productNumber,
            url: `${product?.uri}`,
        })),
    } as any;

    const productListData = {
        "@context": "https://schema.org/",
        "@type": "ItemList",
        itemListElement: products?.map((product: any) => ({
            "@type": "Product",
            name: product?.productNumber,
            image: product?.largeImagePath,
            description: product?.productTitle,
            brand: {
                "@type": "Brand",
                name: product?.brand?.name ?? "",
            },
            offers: {
                "@type": "Offer",
                url: catalogPageState.value.uri,
                priceCurrency: "USD",
                price:
                    productInfosByProductId != null
                        ? productInfosByProductId[product?.id]?.pricing?.actualPrice ?? 0
                        : 0,
            },
        })),
    } as any;

    const breadCrums = catalogPageState.value.breadCrumbs;
    const breadCrumbListData = {
        "@context": "https://schema.org/",
        "@type": "BreadcrumbList",
        itemListElement: breadCrums?.map((subCategory: any, index: number) => ({
            "@type": "ListItem",
            position: index + 1,
            name: subCategory?.text,
            item: `${currentUrl}${subCategory?.url != null ? subCategory?.url : ""}`,
        })),
    } as any;

    const organizationData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Iewc",
        alternateName: "",
        url: `${currentUrl}`,
        logo: `${logo}`,
        sameAs: [""],
    } as any;

    injectMetadata(JSON.stringify(itemListData), "productList_itemListSchema");
    injectMetadata(JSON.stringify(productListData), "productList_ProductSchema");
    injectMetadata(JSON.stringify(breadCrumbListData), "productList_BreadCrumbListSchema");
    injectMetadata(JSON.stringify(organizationData), "productList_OrganizationSchema");

    setWebsiteMetadata({ currentUrl });
};

export const setBrandListMetadata = (props: any) => {
    const { allBrandsDataView, currentUrl, currentPage, location, catalogPageState } = props;

    let logo = "";
    if (typeof document !== "undefined") {
        logo = document.querySelector(".home-page-link img")?.getAttribute("src") || "";
    }

    const itemBrandListData = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "List of Brands",
        description: "",
        url: `${currentUrl}${location.pathname}`,
        numberOfItems: allBrandsDataView.value.length,
        itemListElement: allBrandsDataView.value.map((brand: any, index: number) => ({
            "@type": "ListItem",
            position: index + 1,
            url: `${brand.detailPagePath}`,
            name: brand.name,
            item: {
                "@type": "Brand",
                name: brand.name,
                logo: brand.logoLargeImagePath ? brand.logoLargeImagePath : brand.logoSmallImagePath,
                description: brand.metaDescription,
            },
        })),
    } as any;

    const organizationData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Iewc",
        alternateName: "",
        url: `${currentUrl}`,
        logo: `${logo}`,
        sameAs: [""],
    } as any;

    const breadCrumbs = catalogPageState.id;
    const breadCrumbListData = {
        "@context": "https://schema.org/",
        "@type": "BreadcrumbList",
        itemListElement: [
            {
                "@type": "ListItem",
                position: 1,
                name: "Brands",
                item: `${currentUrl}${breadCrumbs != null ? breadCrumbs : ""}`,
            },
        ],
    } as any;

    const MAX_ITEMS_PER_SCRIPT = 65;
    const chunks = [];

    for (let i = 0; i < allBrandsDataView.value.length; i += MAX_ITEMS_PER_SCRIPT) {
        const chunk = allBrandsDataView.value.slice(i, i + MAX_ITEMS_PER_SCRIPT).map((brand: any, index: number) => ({
            "@type": "ListItem",
            position: i + index + 1,
            url: `${brand.detailPagePath}`,
            name: brand.name,
            item: {
                "@type": "Brand",
                name: brand.name,
                logo: brand.logoLargeImagePath ? brand.logoLargeImagePath : brand.logoSmallImagePath,
                description: brand.metaDescription,
            },
        }));
        chunks.push(chunk);
    }

    chunks.forEach((chunk, idx) => {
        const itemBrandListDataChunk = {
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: `List of Brands (Part ${idx + 1})`,
            url: `${currentUrl}${location.pathname}`,
            numberOfItems: chunk.length,
            itemListElement: chunk,
        };
    
        injectMetadata(JSON.stringify(itemBrandListDataChunk), `brandList_ItemListSchema_${idx}`);
    });

    injectMetadata(JSON.stringify(organizationData), "brandList_OrganizationSchema");
    injectMetadata(JSON.stringify(breadCrumbListData), "brandList_BreadCrumbListSchema");
    // injectMetadata(JSON.stringify(itemBrandListData), "brandList_ItemListSchema");
    setWebsiteMetadata({ currentUrl });
};

export const setCategoryMetadata = (props: any) => {
    const { subCategories, currentUrl } = props;
    let logo = "";
    if (typeof document !== "undefined") {
        logo = document.querySelector(".home-page-link img")?.getAttribute("src") || "";
    }

    const itemListData = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: subCategories?.map((subCategory: any, index: number) => ({
            "@type": "ListItem",
            position: index + 1,
            name: subCategory?.name,
            url: `${currentUrl}${subCategory?.path}`,
        })),
    } as any;

    const breadCrumbListData = {
        "@context": "https://schema.org/",
        "@type": "BreadcrumbList",
        itemListElement: subCategories?.map((subCategory: any, index: number) => ({
            "@type": "ListItem",
            position: index + 1,
            name: subCategory?.name,
            item: `${currentUrl}${subCategory?.path}`,
        })),
    } as any;

    const organizationData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Iewc",
        alternateName: "",
        url: `${currentUrl}`,
        logo: `${logo}`,
        sameAs: [""],
    } as any;

    injectMetadata(JSON.stringify(itemListData), "categoryListItemList");
    injectMetadata(JSON.stringify(breadCrumbListData), "categoryListBreadCrumbList");
    injectMetadata(JSON.stringify(organizationData), "categoryDetailOrganization");
    setWebsiteMetadata({ currentUrl });
};

export const setHomePageMetadata = (props: any) => {
    const { currentUrl } = props;

    let logo = "";
    if (typeof document !== "undefined") {
        logo = document.querySelector(".home-page-link img")?.getAttribute("src") || "";
    }

    const organizationData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Iewc",
        alternateName: "",
        url: `${currentUrl}`,
        logo: `${logo}`,
        sameAs: [""],
    } as any;

    const anchors = document.querySelectorAll("a.verndaleGA4__popular-category");

    if (anchors && anchors.length > 0) {
        const itemListData = {
            "@context": "https://schema.org",
            "@type": "ItemList",
            itemListElement: Array.from(anchors)
                .filter((anchor: any) => {
                    const href = anchor.getAttribute("href");
                    return href && !href.includes("/services");
                })
                .map((anchor: any, index: number) => ({
                    "@type": "ListItem",
                    position: index + 1,
                    name: anchor.textContent?.trim(),
                    url: anchor.getAttribute("href"),
                })),
        } as any;

        injectMetadata(JSON.stringify(itemListData), "homePage_ItemListSchema");
    }

    injectMetadata(JSON.stringify(organizationData), "homePage_OrganizationSchema");
    setWebsiteMetadata({ currentUrl });
};

export const setContactUsPageMetadata = (props: any) => {
    const { currentUrl, currentPage } = props;

    const organizationData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Iewc",
        alternateName: "",
    } as any;

    const breadCrumbs = currentPage.fields.urlSegment;
    const breadCrumbListData = {
        "@context": "https://schema.org/",
        "@type": "BreadcrumbList",
        itemListElement: [
            {
                "@type": "ListItem",
                position: 1,
                name: "Contact Us",
                item: `${currentUrl}`,
            },
        ],
    } as any;

    injectMetadata(JSON.stringify(organizationData), "contactUsPage_OrganizationSchema");
    injectMetadata(JSON.stringify(breadCrumbListData), "contactUsPage_BreadCrumbListSchema");
    setWebsiteMetadata({ currentUrl });
};

export const setRequestQuotePageMetadata = (props: any) => {
    const { currentUrl, currentPage } = props;

    const organizationData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Iewc",
        alternateName: "",
    } as any;

    const breadCrumbs = currentPage.fields.urlSegment;
    const breadCrumbListData = {
        "@context": "https://schema.org/",
        "@type": "BreadcrumbList",
        itemListElement: [
            {
                "@type": "ListItem",
                position: 1,
                name: "Request a Quote",
                item: `${currentUrl}`,
            },
        ],
    } as any;

    injectMetadata(JSON.stringify(organizationData), "requestQuotePage_OrganizationSchema");
    injectMetadata(JSON.stringify(breadCrumbListData), "requestQuotePage_BreadCrumbListSchema");
    setWebsiteMetadata({ currentUrl });
};

export const setLocationFinderPageMetadata = (props: any) => {
    const { locations, currentUrl, currentPage } = props;

    const organizationData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Iewc",
        alternateName: "",
    } as any;

    const breadCrumbListData = {
        "@context": "https://schema.org/",
        "@type": "BreadcrumbList",
        itemListElement: locations?.map((location: any, index: number) => ({
            "@type": "ListItem",
            position: index + 1,
            name: location.name,
            item: location.uri,
        })),
    } as any;

    const itemListData = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: locations?.map((location: any, index: number) => ({
            "@type": "ListItem",
            position: index + 1,
            name: location.name,
            url: location.uri,
        })),
    } as any;

    const locationBusinessData = {
        "@context": "https://schema.org/",
        "@type": "ItemList",
        itemListElement: locations?.map((location: any, index: number) => ({
            "@type": "LocalBusiness",
            name: location.name,
            url: location.uri,
            address: {
                "@type": "PostalAddress",
                streetAddress: `${location.address1} ${location.address2}`,
                addressLocality: location.city,
                addressRegion: location.state,
                postalCode: location.postalCode,
            },
            telephone: location.phone,
        })),
    };

    injectMetadata(JSON.stringify(organizationData), "locationFinderPage_OrganizationSchema");
    injectMetadata(JSON.stringify(breadCrumbListData), "locationFinderPage_BreadCrumbListSchema");
    injectMetadata(JSON.stringify(itemListData), "locationFinderPage_ItemListSchema");
    injectMetadata(JSON.stringify(locationBusinessData), "locationFinderPage_LocationBusinessSchema");
    setWebsiteMetadata({ currentUrl });
};

export const setStandardPageMetadata = (props: any) => {
    const { locations, currentUrl, currentPage } = props;
    let logo = "";
    if (typeof document !== "undefined") {
        logo = document.body.querySelector(".home-page-link img")?.getAttribute("src") || "";
    }

    const organizationData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Iewc",
        alternateName: "",
    } as any;

    const breadCrumbs = currentPage.fields.urlSegment;
    const breadCrumbListData = {
        "@context": "https://schema.org/",
        "@type": "BreadcrumbList",
        itemListElement: [
            {
                "@type": "ListItem",
                position: 1,
                name: "Standard Page",
                item: `${currentUrl}`,
            },
        ],
    } as any;

    if (currentPage.type === "BlogDetailPage") {
        const articleData = {
            "@context": "https://schema.org",
            "@type": "Article",
            mainEntityOfPage: {
                "@type": "WebPage",
                "@id": `${currentUrl}`,
            },
            headline: currentPage.fields.title,
            description: currentPage.fields.summary,
            image: currentPage.fields.thumbnail,
            author: {
                "@type": "Person",
                name: currentPage.fields.author,
                url: currentPage.fields.authorUrl.value,
            },
            publisher: {
                "@type": "Organization",
                name: "Iewc",
                logo: {
                    "@type": "ImageObject",
                    url: logo,
                },
            },
            datePublished: currentPage.fields.publishDate,
        } as any;

        injectMetadata(JSON.stringify(articleData), "standardPage_ArticleSchema");
    }

    injectMetadata(JSON.stringify(organizationData), "standardPage_OrganizationSchema");
    injectMetadata(JSON.stringify(breadCrumbListData), "standardPage_BreadCrumbListSchema");
    setWebsiteMetadata({ currentUrl });
};

export const setWebsiteMetadata = (props: any) => {
    const { currentUrl, searchTerm, searchUrl } = props;
    let logo = "";
    if (typeof document !== "undefined") {
        logo = document.querySelector(".home-page-link img")?.getAttribute("src") || "";
    }
    const websiteData = {
        "@context": "https://schema.org/",
        "@type": "WebSite",
        name: "Iewc",
        url: `${currentUrl}`,
        potentialAction: {
            "@type": "SearchAction",
            target: searchTerm ? `${searchUrl}` : "",
            "query-input": searchTerm ? `required name=${searchTerm}` : "",
        },
    } as any;

    injectMetadata(JSON.stringify(websiteData), "websiteDataSchema");
};
